<script setup lang="ts">
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
import { UiDialog, UiButton, UiFieldContainer, UiText, UiChip } from '@shopexperts/shared';
import type { ExpertFilterPreset } from '@shopexperts/legacy-api';
import {
  describeActiveExpertFilters,
  type ExpertManagerFilterState,
} from '@/app/composables/expert-manager/expertManagerFilters';

const props = defineProps<{
  mode: 'create' | 'rename';
  preset?: ExpertFilterPreset | null;
  /** Filters that will be stored — only meaningful in `create` mode. */
  filters: ExpertManagerFilterState;
  tagNameById: Map<number, string>;
  loading?: boolean;
  existingNames: string[];
}>();

const open = defineModel<boolean>({ default: false });

const emit = defineEmits<{
  submit: [name: string];
}>();

const name = ref('');
const touched = ref(false);
const inputRef = useTemplateRef<HTMLInputElement>('nameField');

const trimmedName = computed(() => name.value.replace(/\s+/g, ' ').trim());

const validationError = computed(() => {
  if (!trimmedName.value) return 'Give the tab a name.';
  if (trimmedName.value.length > 100) return 'Keep the name under 100 characters.';
  // The API enforces case-insensitive uniqueness per admin; catch it before the round trip.
  const clash = props.existingNames.some(
    (existing) => existing.toLowerCase() === trimmedName.value.toLowerCase()
      && existing.toLowerCase() !== (props.preset?.name ?? '').toLowerCase(),
  );
  if (clash) return 'You already have a tab with this name.';
  return null;
});

const fieldError = computed(() => (touched.value ? validationError.value : null));

const summary = computed(() => describeActiveExpertFilters(props.filters, props.tagNameById));

watch(open, (isOpen) => {
  if (!isOpen) return;
  name.value = props.mode === 'rename' ? (props.preset?.name ?? '') : '';
  touched.value = false;
  nextTick(() => inputRef.value?.focus());
});

function submit() {
  touched.value = true;
  if (validationError.value) return;
  emit('submit', trimmedName.value);
}
</script>

<template>
  <UiDialog
    v-model="open"
    :title="mode === 'rename' ? 'Rename tab' : 'Save filters as a tab'"
    size="md"
  >
    <div class="flex flex-col gap-5">
      <UiText
        as="p"
        variant="p-small"
        class="text-se-gray-600"
        :text="mode === 'rename'
          ? 'Only the name changes — the saved filters stay as they are.'
          : 'The current filters become a tab in your expert list. Tabs are private to your admin account.'"
      />

      <div class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Tab name" for="expert-preset-name" />
        <UiFieldContainer
          :invalid="!!fieldError"
          :error="fieldError ?? undefined"
          describedByIdBase="expert-preset-name"
          @focus="inputRef?.focus()"
        >
          <input
            id="expert-preset-name"
            ref="nameField"
            v-model="name"
            :disabled="loading"
            type="text"
            placeholder="Unclaimed priority experts"
            class="min-w-0 flex-1 text-sm text-se-dark"
            :aria-invalid="fieldError ? 'true' : undefined"
            @input="touched = true"
            @keyup.enter="submit"
          />
        </UiFieldContainer>
      </div>

      <div v-if="mode === 'create'" class="flex flex-col gap-2">
        <UiText as="span" variant="caption" class="tracking-wide text-gray-400">SAVED FILTERS</UiText>
        <div v-if="filters.search || summary.length" class="flex flex-wrap gap-1.5">
          <UiChip
            v-if="filters.search"
            :text="`Search: ${filters.search}`"
            variant="default"
            size="xs"
            rounded="small"
          />
          <UiChip
            v-for="item in summary"
            :key="item.key"
            :text="item.label"
            variant="default"
            size="xs"
            rounded="small"
          />
        </div>
        <UiText v-else as="p" variant="p-small" class="text-gray-400">
          No filters applied — this tab would show every expert.
        </UiText>
      </div>
    </div>

    <template #footer>
      <div class="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <UiButton variant="outline" label="Cancel" :disabled="loading" @click="open = false" />
        <UiButton
          variant="primary"
          :label="loading ? 'Saving…' : mode === 'rename' ? 'Rename' : 'Save tab'"
          :disabled="loading || (touched && !!validationError)"
          @click="submit"
        />
      </div>
    </template>
  </UiDialog>
</template>
